import type { MidiStatus as MidiStatusValue } from '../../../shared/hooks/useMidiDrums';

export interface GameDrum {
  id: string;
  ko: string;
  en: string;
  color: string;
}

export const DRUMS: GameDrum[] = [
  { id: 'crash', ko: '빨간색', en: 'Red', color: '#ff0000' },
  { id: 'tom1', ko: '주황색', en: 'Orange', color: '#ff7800' },
  { id: 'tom2', ko: '노란색', en: 'Yellow', color: '#fee500' },
  { id: 'ride', ko: '초록색', en: 'Green', color: '#00b84a' },
  { id: 'snare', ko: '파란색', en: 'Blue', color: '#006bff' },
  { id: 'hihat', ko: '분홍색', en: 'Pink', color: '#ff3fa4' },
  { id: 'floor', ko: '보라색', en: 'Purple', color: '#843dff' },
];

export interface MemoryAgeProfile {
  id: string;
  ko: string;
  en: string;
  detailKo: string;
  trials: number;
  startSpan: number;
  maxSpan: number;
  drumCount: number;
  cardMs: number;
  gapMs: number;
  answerLimit: number;
  speedReference: number;
}

export const MEMORY_AGE_PROFILES: MemoryAgeProfile[] = [
  { id: 'preschool', ko: '유아', en: 'Preschool', detailKo: '5~6세 · 천천히 시작', trials: 5, startSpan: 1, maxSpan: 3, drumCount: 3, cardMs: 1500, gapMs: 1000, answerLimit: 8, speedReference: 1400 },
  { id: 'elementary', ko: '초등', en: 'Elementary', detailKo: '7~12세 · 기본 순서 기억', trials: 5, startSpan: 1, maxSpan: 4, drumCount: 4, cardMs: 1300, gapMs: 1200, answerLimit: 10, speedReference: 1200 },
  { id: 'teen', ko: '청소년', en: 'Teen', detailKo: '13~18세 · 확장 순서 기억', trials: 5, startSpan: 2, maxSpan: 5, drumCount: 5, cardMs: 1150, gapMs: 1400, answerLimit: 12, speedReference: 1050 },
  { id: 'adult', ko: '성인', en: 'Adult', detailKo: '19~64세 · 6개까지 기억', trials: 5, startSpan: 2, maxSpan: 6, drumCount: 7, cardMs: 1000, gapMs: 1500, answerLimit: 12, speedReference: 900 },
  { id: 'older', ko: '고령', en: 'Older adult', detailKo: '65세+ · 여유 있는 입력', trials: 5, startSpan: 1, maxSpan: 5, drumCount: 4, cardMs: 1500, gapMs: 1800, answerLimit: 15, speedReference: 1450 },
];

export interface ExecutiveProfile {
  id: string;
  ko: string;
  en: string;
  trials: number;
  colors: number;
  limit: number;
  ref: number;
}

export const EXECUTIVE_PROFILES: ExecutiveProfile[] = [
  { id: 'preschool', ko: '유아', en: 'Preschool', trials: 5, colors: 3, limit: 8, ref: 1400 },
  { id: 'elementary', ko: '초등', en: 'Elementary', trials: 5, colors: 4, limit: 9, ref: 1200 },
  { id: 'teen', ko: '청소년', en: 'Teen', trials: 5, colors: 5, limit: 10, ref: 1050 },
  { id: 'adult', ko: '성인', en: 'Adult', trials: 5, colors: 7, limit: 10, ref: 900 },
  { id: 'older', ko: '고령', en: 'Older adult', trials: 5, colors: 4, limit: 13, ref: 1450 },
];

export type ExecutiveGameType = 'stroop' | 'rule';

export const EXECUTIVE_CONFIG: Record<ExecutiveGameType, {
  id: string; titleKo: string; titleEn: string; subtitleKo: string; subtitleEn: string; domainKo: string; domainEn: string;
}> = {
  stroop: {
    id: 'stroop',
    titleKo: '스트룹 훈련',
    titleEn: 'Stroop Training',
    subtitleKo: '글자의 뜻이 아닌 글자 색상과 같은 드럼을 선택하세요.',
    subtitleEn: 'Choose the drum that matches the ink color, not the word.',
    domainKo: '억제 통제 · 선택적 주의',
    domainEn: 'INHIBITORY CONTROL · SELECTIVE ATTENTION',
  },
  rule: {
    id: 'rule-change',
    titleKo: '색깔 규칙 찾기',
    titleEn: 'Find the Color Rule',
    subtitleKo: '색깔이 나오는 순서를 보고, 물음표에 들어갈 다음 색깔을 골라 보세요.',
    subtitleEn: 'Look at the color pattern and choose the color that comes next.',
    domainKo: '규칙 찾기 · 생각 바꾸기',
    domainEn: 'PATTERN FINDING · FLEXIBLE THINKING',
  },
};

export function seededNumber(seed: number) {
  let value = seed + 1831565813;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
}

export function makeMemorySequence(profile: MemoryAgeProfile, trialIndex: number, span: number): string[] {
  const available = DRUMS.slice(0, profile.drumCount);
  const result: string[] = [];
  for (let index = 0; index < span; index += 1) {
    const offset = Math.floor(seededNumber((trialIndex + 19) * 97 + (index + 3) * 131 + span * 37) * available.length);
    const previousId = result[index - 1];
    const candidate = available[offset];
    result.push(available[(offset + (candidate.id === previousId ? 1 : 0)) % available.length].id);
  }
  return result;
}

export interface RuleTrial {
  rule: 'next-color' | 'alternating';
  values: GameDrum[];
  expected: GameDrum;
}

export function buildRuleSequence(profile: ExecutiveProfile, index: number): RuleTrial {
  const list = DRUMS.slice(0, profile.colors);
  if (index < 3) {
    const start = (index * 2) % list.length;
    return {
      rule: 'next-color',
      values: [0, 1, 2, 3].map((step) => list[(start + step) % list.length]),
      expected: list[(start + 4) % list.length],
    };
  }
  const first = list[index % list.length];
  const second = list[(index + 1) % list.length];
  return { rule: 'alternating', values: [first, second, first, second], expected: first };
}

export function playCognitiveAnswerTone(result: 'correct' | 'wrong') {
  try {
    const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;
    const context = new AudioContextCtor();
    const notes = result === 'correct' ? [523.25, 659.25, 783.99] : [246.94, 196];
    notes.forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = context.currentTime + index * 0.07;
      oscillator.type = result === 'correct' ? 'sine' : 'triangle';
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.055, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.15);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.16);
    });
    window.setTimeout(() => context.close().catch(() => {}), 500);
  } catch {
    // Web Audio unsupported/blocked — the visual answer effect still plays.
  }
}

export function midiStatusLabel(language: string, status: MidiStatusValue) {
  if (language === 'ko') {
    if (status === 'connected') return 'MIDI 드럼 연결됨';
    if (status === 'checking') return 'MIDI 드럼 확인 중';
    return '화면 드럼으로 실행';
  }
  if (status === 'connected') return 'MIDI CONNECTED';
  if (status === 'checking') return 'CHECKING MIDI';
  return 'TOUCH DRUM MODE';
}

export function MidiStatus({ language, status, deviceName, onScan }: { language: string; status: MidiStatusValue; deviceName: string; onScan: () => void }) {
  const connected = status === 'connected';
  const label = midiStatusLabel(language, status);
  return (
    <button type="button" className={`midi-status-chip ${connected ? 'connected' : 'disconnected'}`} onClick={onScan} title={deviceName || label}>
      <i aria-hidden="true" /><span>{label}</span>
    </button>
  );
}
